import React, { useState, useCallback, useRef, useEffect } from 'react';
import axiosInstance from '../config/axios';
import './MissingItemTicketModal.css';

const MAX_PHOTO_BYTES = 5 * 1024 * 1024;
const ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp'];
const TYPE_MISSING = 'missing_item';
const TYPE_CHANGE = 'item_change_request';

/**
 * Shared create modal for site and manager inventory tickets.
 * mode: 'site' | 'manager'
 */
const MissingItemTicketModal = ({
  open,
  onClose,
  onSubmitted,
  mode = 'site',
  submitUrl = '/api/tenant/site/missing-item-tickets',
  optionsUrl = '/api/tenant/site/missing-item-tickets/inventory-ticket-options',
  itemsUrl = '/api/tenant/manager/missing-item-tickets/inventory-ticket-items'
}) => {
  const [ticketType, setTicketType] = useState(TYPE_MISSING);
  const [itemDescription, setItemDescription] = useState('');
  const [requestedChange, setRequestedChange] = useState('');
  const [inventoryItemId, setInventoryItemId] = useState('');
  const [brandId, setBrandId] = useState('');
  const [notes, setNotes] = useState('');
  const [photo, setPhoto] = useState(null);
  const [photoName, setPhotoName] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState(null);
  const [brands, setBrands] = useState([]);
  const [items, setItems] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const resetForm = useCallback(() => {
    setTicketType(TYPE_MISSING);
    setItemDescription('');
    setRequestedChange('');
    setInventoryItemId('');
    setBrandId('');
    setNotes('');
    setPhoto(null);
    setPhotoName('');
    setError('');
    setSuccess(false);
    setPhotoPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  }, []);

  const handleClose = useCallback(() => {
    if (!submitting) {
      resetForm();
      onClose?.();
    }
  }, [onClose, resetForm, submitting]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const loadOptions = async () => {
      setOptionsLoading(true);
      setError('');
      try {
        const res = await axiosInstance.get(optionsUrl);
        if (cancelled) return;
        if (mode === 'manager') {
          setBrands(res.data.brands || []);
          setItems([]);
        } else {
          setItems(res.data.items || []);
          setBrands([]);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || 'Failed to load ticket options.');
        }
      } finally {
        if (!cancelled) setOptionsLoading(false);
      }
    };
    loadOptions();
    return () => {
      cancelled = true;
    };
  }, [open, optionsUrl, mode]);

  useEffect(() => {
    if (!open || mode !== 'manager' || !brandId) {
      if (mode === 'manager') setItems([]);
      return undefined;
    }
    let cancelled = false;
    const loadItems = async () => {
      setItemsLoading(true);
      try {
        const res = await axiosInstance.get(`${itemsUrl}?brandId=${encodeURIComponent(brandId)}`);
        if (cancelled) return;
        setItems(res.data.items || []);
      } catch (err) {
        if (!cancelled) {
          setItems([]);
          setError(err.response?.data?.error || 'Failed to load inventory items.');
        }
      } finally {
        if (!cancelled) setItemsLoading(false);
      }
    };
    loadItems();
    return () => {
      cancelled = true;
    };
  }, [open, mode, brandId, itemsUrl]);

  const clearPhotoInputs = () => {
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const clearPreview = () => {
    setPhotoPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const handlePhotoChange = (e) => {
    setError('');
    const file = e.target.files && e.target.files[0];
    if (!file) {
      setPhoto(null);
      setPhotoName('');
      clearPreview();
      return;
    }
    if (!ALLOWED_MIME.includes(file.type)) {
      setError('Photo must be a JPEG, PNG, or WEBP image.');
      setPhoto(null);
      setPhotoName('');
      clearPreview();
      clearPhotoInputs();
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      setError('Photo must be 5MB or smaller.');
      setPhoto(null);
      setPhotoName('');
      clearPreview();
      clearPhotoInputs();
      return;
    }
    setPhoto(file);
    setPhotoName(file.name);
    setPhotoPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (mode === 'manager' && !brandId) {
      setError('Please select a brand.');
      return;
    }

    if (ticketType === TYPE_MISSING) {
      const desc = (itemDescription || '').trim();
      if (!desc) {
        setError('Item name / description is required.');
        return;
      }
    } else {
      if (!inventoryItemId) {
        setError('Please select an inventory item.');
        return;
      }
      if (!(requestedChange || '').trim()) {
        setError('Please describe the requested change.');
        return;
      }
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('ticketType', ticketType);
      if (mode === 'manager') formData.append('brandId', brandId);
      if (ticketType === TYPE_MISSING) {
        formData.append('itemDescription', itemDescription.trim());
      } else {
        formData.append('inventoryItemId', inventoryItemId);
        formData.append('requestedChange', requestedChange.trim());
      }
      if (notes.trim()) formData.append('notes', notes.trim());
      if (photo) formData.append('photo', photo);
      await axiosInstance.post(submitUrl, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSuccess(true);
      onSubmitted?.();
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || 'Failed to send ticket. Please try again.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const noManagerBrands = mode === 'manager' && !optionsLoading && brands.length === 0;

  return (
    <div className="mit-modal-overlay" onClick={handleClose} role="dialog" aria-modal="true" aria-labelledby="mit-modal-title">
      <div className="mit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mit-modal-header">
          <h2 id="mit-modal-title" className="mit-modal-title">Report Inventory Issue</h2>
          <button type="button" className="mit-modal-close" onClick={handleClose} aria-label="Close">&times;</button>
        </div>
        {success ? (
          <p className="mit-modal-success">
            Your inventory ticket has been sent to the admin team.
          </p>
        ) : (
          <form className="mit-modal-form" onSubmit={handleSubmit}>
            <p className="mit-modal-explanation">
              Report a missing inventory item or request a change to an existing item. The admin team will review your ticket.
            </p>

            <div className="mit-modal-form-group">
              <span className="mit-modal-fieldset-label">Ticket type</span>
              <div className="mit-ticket-type-row" role="radiogroup" aria-label="Ticket type">
                <label className="mit-ticket-type-option">
                  <input
                    type="radio"
                    name="mit-ticket-type"
                    value={TYPE_MISSING}
                    checked={ticketType === TYPE_MISSING}
                    onChange={() => setTicketType(TYPE_MISSING)}
                  />
                  Missing item
                </label>
                <label className="mit-ticket-type-option">
                  <input
                    type="radio"
                    name="mit-ticket-type"
                    value={TYPE_CHANGE}
                    checked={ticketType === TYPE_CHANGE}
                    onChange={() => setTicketType(TYPE_CHANGE)}
                  />
                  Item change request
                </label>
              </div>
            </div>

            {mode === 'manager' && (
              <div className="mit-modal-form-group">
                <label htmlFor="mit-brand">Brand</label>
                {noManagerBrands ? (
                  <p className="mit-modal-empty-hint">No brands are available for the sites you manage.</p>
                ) : (
                  <select
                    id="mit-brand"
                    className="mit-modal-input"
                    value={brandId}
                    onChange={(e) => {
                      setBrandId(e.target.value);
                      setInventoryItemId('');
                    }}
                    required
                    disabled={optionsLoading}
                  >
                    <option value="">Select a brand…</option>
                    {brands.map((b) => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {ticketType === TYPE_MISSING ? (
              <div className="mit-modal-form-group">
                <label htmlFor="mit-item-description">Item name / description</label>
                <textarea
                  id="mit-item-description"
                  value={itemDescription}
                  onChange={(e) => setItemDescription(e.target.value)}
                  required
                  rows={3}
                  className="mit-modal-input"
                  placeholder="e.g. Red Bull Sugar Free 250ml"
                />
              </div>
            ) : (
              <>
                <div className="mit-modal-form-group">
                  <label htmlFor="mit-inventory-item">Inventory item</label>
                  <select
                    id="mit-inventory-item"
                    className="mit-modal-input"
                    value={inventoryItemId}
                    onChange={(e) => setInventoryItemId(e.target.value)}
                    required
                    disabled={optionsLoading || itemsLoading || (mode === 'manager' && !brandId)}
                  >
                    <option value="">
                      {mode === 'manager' && !brandId
                        ? 'Select a brand first…'
                        : itemsLoading
                          ? 'Loading items…'
                          : 'Select an item…'}
                    </option>
                    {items.map((item) => (
                      <option key={item.id} value={item.id}>{item.label || item.itemName}</option>
                    ))}
                  </select>
                  {mode === 'site' && !optionsLoading && items.length === 0 && (
                    <p className="mit-modal-empty-hint">No inventory items are available for this site.</p>
                  )}
                </div>
                <div className="mit-modal-form-group">
                  <label htmlFor="mit-requested-change">What needs to change?</label>
                  <textarea
                    id="mit-requested-change"
                    value={requestedChange}
                    onChange={(e) => setRequestedChange(e.target.value)}
                    required
                    rows={3}
                    className="mit-modal-input"
                    placeholder="e.g. Pack size should be 24, not 12"
                  />
                </div>
              </>
            )}

            <div className="mit-modal-form-group">
              <label htmlFor="mit-notes">Notes (optional)</label>
              <textarea
                id="mit-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="mit-modal-input"
                placeholder="Any extra detail that could help the admin team"
              />
            </div>
            <div className="mit-modal-form-group">
              <label>Photo (optional)</label>
              <input
                id="mit-photo"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handlePhotoChange}
                ref={fileInputRef}
                className="mit-modal-file-input-hidden"
              />
              <input
                id="mit-photo-camera"
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handlePhotoChange}
                ref={cameraInputRef}
                className="mit-modal-file-input-hidden"
              />
              <div className="mit-photo-actions">
                <button
                  type="button"
                  className="btn btn-secondary mit-photo-action-btn"
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                >
                  Choose photo
                </button>
                <button
                  type="button"
                  className="btn btn-secondary mit-photo-action-btn"
                  onClick={() => cameraInputRef.current && cameraInputRef.current.click()}
                >
                  Take photo
                </button>
              </div>
              {photoName && <p className="mit-modal-photo-name">Selected: {photoName}</p>}
              {photoPreviewUrl && (
                <img src={photoPreviewUrl} alt="Selected preview" className="mit-modal-photo-preview" />
              )}
            </div>
            {error && <div className="mit-modal-error">{error}</div>}
            <div className="mit-modal-actions">
              <button type="button" className="btn btn-secondary" onClick={handleClose} disabled={submitting}>Cancel</button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitting || noManagerBrands || optionsLoading}
              >
                {submitting ? 'Sending…' : 'Submit Ticket'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default MissingItemTicketModal;
